import { Button, Grid, Typography } from "@material-ui/core";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { ToastMessage } from "../../../components";
import type { MxQuickWin } from "../../../models/gameplan";
import { MentorTipIcon, SuccessIcon } from "../../../shared/icons/gamePlan";
import { setQuickWinStatus } from "../../learningPath.service";
import { quickwinHeaderStyles } from "./quickwin-header.styles";

interface QuickwinHeaderProps {
  quickwin: string;
  id: number;
  goToQuickwins: (quickWin: MxQuickWin) => void;
  completedQuickWin?: boolean;
}
export const QuickwinHeader = ({
  quickwin,
  id,
  goToQuickwins,
  completedQuickWin,
}: QuickwinHeaderProps) => {
  const classes = quickwinHeaderStyles();
  const [accountNotConnected, setAccountNotConnected] = useState(false);
  const [content, setContent] = useState({
    oneOnOne: {
      message: <>Quick win: Schedule your first 1:1</>,
      later: true,
      successMessage: null,
    },
  });

  useEffect(() => {
    if (completedQuickWin) {
      setContent((prevState) => ({
        ...prevState,
        oneOnOne: {
          ...prevState.oneOnOne,
          later: false,
          successMessage: (
            <div className={classes.successContainer}>
              <MentorTipIcon className={classes.mentorTipIcon} />
              <SuccessIcon />
              <span className={classes.successMessage}>
                You&apos;ve successfully scheduled your first 1:1 meeting!
              </span>
            </div>
          ),
        },
      }));
    }
  }, [
    completedQuickWin,
    classes.successContainer,
    classes.mentorTipIcon,
    classes.successMessage,
  ]);

  const queryClient = useQueryClient();
  const { mutate } = useMutation(setQuickWinStatus, {
    onSuccess: () => {
      queryClient.invalidateQueries(["quickwins"]);
    },
  });

  const handleStatusChange = (idTochange, status) => {
    mutate({ id: idTochange, status });
    goToQuickwins({ quickWin: "wizard", id: undefined });
  };

  const isQuickWinCompleted = !!content[quickwin].successMessage;

  const nextButton = () => {
    return (
      <Button
        variant="contained"
        color="primary"
        endIcon={<ChevronRightIcon />}
        className={clsx(
          {
            [classes.disabledNextButton]: !isQuickWinCompleted,
          },
          classes.nextButton
        )}
        onClick={() => {
          handleStatusChange(id, "Completed");
        }}
      >
        Next quick win
      </Button>
    );
  };

  return (
    <Grid
      className={clsx(
        { [classes.successBackground]: isQuickWinCompleted },
        classes.container
      )}
      container
      direction="row"
      justifyContent="space-between"
      alignItems="center"
    >
      {isQuickWinCompleted ? (
        <Grid item>{content[quickwin].successMessage}</Grid>
      ) : (
        <Grid item>
          <span className={classes.quickWinText}>Quick win: </span>
          <span className={classes.message}>{content[quickwin].message}</span>
        </Grid>
      )}
      <Grid item className={classes.buttonsContainer}>
        {content[quickwin].later && (
          <div
            onClick={() => goToQuickwins({ quickWin: "wizard", id: undefined })}
            className={classes.link}
          >
            <Typography className={classes.laterButton}>Do it later</Typography>
          </div>
        )}
        {nextButton()}
      </Grid>
      <ToastMessage
        isOpen={accountNotConnected}
        severity="error"
        message="Your account could not be connected"
        handleClose={() => setAccountNotConnected(false)}
      />
    </Grid>
  );
};

QuickwinHeader.defaultProps = {
  completedQuickWin: false,
};
